import { useEffect, useState, useContext } from 'react';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Clock, User, Star, CheckCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { AppContext } from '@/App';

function ActiveTransactions() {
  const { user, updateBalance, API } = useContext(AppContext);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(null);
  const [ratingDialog, setRatingDialog] = useState(null);
  const [rating, setRating] = useState(0);
  const [review, setReview] = useState('');

  useEffect(() => {
    fetchActiveTransactions();
  }, []);

  const fetchActiveTransactions = async () => {
    try {
      const response = await axios.get(`${API}/transactions/my`);
      // Filter for non-completed transactions
      const active = response.data.filter(t => 
        ['accepted', 'awaiting_verification'].includes(t.status)
      );
      setTransactions(active);
    } catch (error) {
      console.error('Error fetching transactions:', error);
      toast.error('Failed to load transactions');
    } finally {
      setLoading(false);
    }
  };

  const handleComplete = async (transactionId) => {
    setProcessing(transactionId);
    try {
      await axios.post(`${API}/transactions/${transactionId}/complete`);
      toast.success('Marked as complete. Waiting for requester verification.');
      fetchActiveTransactions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to complete');
    } finally {
      setProcessing(null);
    }
  };

  const handleVerify = async (transactionId) => {
    setProcessing(transactionId);
    try {
      const response = await axios.post(`${API}/transactions/${transactionId}/verify`);
      toast.success('Transaction verified! Tokens transferred.');
      
      // Update balance
      await updateBalance();
      
      fetchActiveTransactions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to verify');
    } finally {
      setProcessing(null);
    }
  };

  const handleRate = async () => {
    if (!rating) {
      toast.error('Please select a rating');
      return;
    }

    try {
      await axios.post(`${API}/transactions/${ratingDialog.id}/rate`, {
        rating,
        review: review || null
      });
      toast.success('Thank you for your rating!');
      setRatingDialog(null);
      setRating(0);
      setReview('');
      fetchActiveTransactions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit rating');
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      accepted: <Badge className="bg-blue-100 text-blue-800">In Progress</Badge>,
      awaiting_verification: <Badge className="bg-orange-100 text-orange-800">Awaiting Verification</Badge>,
    };
    return badges[status] || <Badge>{status}</Badge>;
  };

  const canComplete = (tx) => tx.status === 'accepted' && tx.to_user_id === user?.id;
  const canVerify = (tx) => tx.status === 'awaiting_verification' && tx.from_user_id === user?.id;
  const canCancel = (tx) => ['accepted', 'awaiting_verification'].includes(tx.status);

  const handleCancel = async (transactionId) => {
    if (!confirm('Are you sure you want to cancel this transaction? Tokens will be returned from escrow.')) {
      return;
    }
    
    setProcessing(transactionId);
    try {
      await axios.post(`${API}/transactions/${transactionId}/cancel`);
      toast.success('Transaction cancelled. Tokens returned from escrow.');
      
      // Update balance
      await updateBalance();
      
      fetchActiveTransactions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel');
    } finally {
      setProcessing(null);
    }
  };

  if (loading) {
    return <div className="text-center py-8">Loading...</div>;
  }

  return (
    <div className="space-y-6" data-testid="active-transactions">
      <div>
        <h1 className="text-4xl font-bold text-gray-800 mb-2">Active Transactions</h1>
        <p className="text-gray-600">Manage ongoing service exchanges</p>
      </div>

      {transactions.length === 0 ? (
        <Card className="bg-white shadow-lg">
          <CardContent className="py-12">
            <p className="text-center text-gray-500">No active transactions</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {transactions.map((tx) => (
            <Card key={tx.id} className="bg-white shadow-lg" data-testid={`transaction-${tx.id}`}>
              <CardHeader>
                <div className="flex items-start justify-between">
                  {getStatusBadge(tx.status)}
                  <div className="flex items-center gap-1 text-purple-600 font-semibold">
                    <Clock className="w-4 h-4" />
                    <span>{tx.time_tokens}h</span>
                  </div>
                </div>
                <CardTitle className="mt-2">{tx.service_title}</CardTitle>
                <CardDescription className="flex items-center gap-1 text-sm">
                  <User className="w-3 h-3" />
                  {tx.from_user_id === user?.id ? `Provider: ${tx.to_user}` : `Requester: ${tx.from_user}`}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-gray-500">
                  Started: {new Date(tx.timestamp).toLocaleDateString()}
                </p>
                {tx.provider_completed_at && (
                  <p className="text-sm text-gray-500 mt-1">
                    Completed: {new Date(tx.provider_completed_at).toLocaleDateString()}
                  </p>
                )}
              </CardContent>
              <CardFooter className="flex flex-col gap-2">
                {canComplete(tx) && (
                  <Button
                    className="w-full bg-blue-600 hover:bg-blue-700"
                    onClick={() => handleComplete(tx.id)}
                    disabled={processing === tx.id}
                    data-testid={`complete-${tx.id}`}
                  >
                    <CheckCircle className="w-4 h-4 mr-2" />
                    {processing === tx.id ? 'Marking Complete...' : 'Mark Work as Completed'}
                  </Button>
                )}
                {canVerify(tx) && (
                  <Button
                    className="w-full bg-green-600 hover:bg-green-700"
                    onClick={() => handleVerify(tx.id)}
                    disabled={processing === tx.id}
                    data-testid={`verify-${tx.id}`}
                  >
                    <CheckCircle className="w-4 h-4 mr-2" />
                    {processing === tx.id ? 'Verifying...' : 'Verify & Release Payment'}
                  </Button>
                )}
                {canCancel(tx) && (
                  <Button
                    variant="outline"
                    className="w-full text-red-600 hover:bg-red-50"
                    onClick={() => handleCancel(tx.id)}
                    disabled={processing === tx.id}
                    data-testid={`cancel-${tx.id}`}
                  >
                    Cancel Transaction
                  </Button>
                )}
              </CardFooter>
            </Card>
          ))}
        </div>
      )}

      {/* Rating Dialog */}
      {ratingDialog && (
        <Dialog open={!!ratingDialog} onOpenChange={() => setRatingDialog(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Rate Transaction</DialogTitle>
              <DialogDescription>Rate your experience with {ratingDialog.other_user}</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <label className="text-sm font-medium mb-2 block">Rating</label>
                <div className="flex gap-2">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      onClick={() => setRating(star)}
                      className="transition-colors"
                      data-testid={`star-${star}`}
                    >
                      <Star
                        className={`w-8 h-8 ${
                          star <= rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'
                        }`}
                      />
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-sm font-medium mb-2 block">Review (Optional)</label>
                <Textarea
                  placeholder="Share your experience..."
                  value={review}
                  onChange={(e) => setReview(e.target.value)}
                  data-testid="review-input"
                />
              </div>
              <Button onClick={handleRate} className="w-full" data-testid="submit-rating">
                Submit Rating
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

export default ActiveTransactions;